// assembly list export (code-split)

import { formatHelmExport, resolveHelm } from '@technohull/shared';
import type { OrderDetail, SpecField } from './orders.js';
import { currentDatePrefs, formatDate, localTodayIso, type DatePrefs } from '../lib/dates.js';
import templateRows from './assemblyTemplate.json';

export type { StandardEquip } from './orders.js';

/* eslint-disable @typescript-eslint/no-explicit-any */

type EquipLine = OrderDetail['snapshot']['equipment'][number];
type TemplateRow = { type: 'section' | 'field'; code?: string; en: string; gr: string };
const TEMPLATE = templateRows as TemplateRow[];

const norm = (s: unknown) => String(s ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
const stripColon = (s: unknown) => String(s ?? '').replace(/[:\s]+$/, '').trim();

/** item text for the export: helm options → structured multi-line; else assembly label / name. */
function itemLabel(e: EquipLine): string {
  const hs = resolveHelm(e);
  return hs ? formatHelmExport(hs) : e.assemblyExportLabel || e.name || `#${e.equipmentId}`;
}

// ── palette + styles (sz 14 throughout, matching the source workbook) ─────────────
const GRAYHDR = 'D8D8D8'; // section band, like the template
const SETUP = 'BFBFBF'; // super-section band (SET UP / EXTRAS)
const VALUEBG = 'FFFDE7'; // faint highlight on the fillable VARIABLE column
const BORDER = 'D0D7DC';
const thin = { style: 'thin', color: { rgb: BORDER } };
const box = { top: thin, bottom: thin, left: thin, right: thin };

const S = {
  title: { font: { bold: true, sz: 18, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '1C2831' } }, alignment: { horizontal: 'center', vertical: 'center' } },
  colhdr: { font: { bold: true, sz: 14 }, alignment: { horizontal: 'center', vertical: 'center' }, border: box },
  colhdrEng: { font: { bold: true, sz: 14 }, alignment: { horizontal: 'right', vertical: 'center' }, border: box },
  colhdrGr: { font: { bold: true, sz: 14 }, alignment: { horizontal: 'left', vertical: 'center' }, border: box },
  super: { font: { bold: true, sz: 14 }, fill: { fgColor: { rgb: SETUP } }, alignment: { horizontal: 'center', vertical: 'center' }, border: box },
  sectionEng: { font: { bold: true, sz: 14 }, fill: { fgColor: { rgb: GRAYHDR } }, alignment: { horizontal: 'center', vertical: 'center' }, border: box },
  sectionMid: { font: { bold: true, sz: 14 }, fill: { fgColor: { rgb: GRAYHDR } }, alignment: { horizontal: 'center', vertical: 'center' }, border: box },
  sectionGr: { font: { bold: true, sz: 14 }, fill: { fgColor: { rgb: GRAYHDR } }, alignment: { horizontal: 'center', vertical: 'center' }, border: box },
  code: { font: { sz: 14, color: { rgb: '8A969C' } }, alignment: { horizontal: 'right', vertical: 'center' }, border: box },
  note: { font: { sz: 12 }, alignment: { horizontal: 'left', vertical: 'center', wrapText: true }, border: box },
  eng: { font: { sz: 14 }, alignment: { horizontal: 'right', vertical: 'center', wrapText: true }, border: box },
  value: { font: { sz: 14, bold: true }, fill: { fgColor: { rgb: VALUEBG } }, alignment: { horizontal: 'center', vertical: 'center', wrapText: true }, border: box },
  gr: { font: { sz: 14, color: { rgb: '55636A' } }, alignment: { horizontal: 'left', vertical: 'center', wrapText: true }, border: box },
  redSection: { font: { bold: true, sz: 14, color: { rgb: 'C0392B' } }, fill: { fgColor: { rgb: 'FCE4E4' } }, alignment: { horizontal: 'center', vertical: 'center' }, border: box },
  redCell: { font: { bold: true, sz: 14, color: { rgb: 'C0392B' } }, alignment: { horizontal: 'left', vertical: 'center', wrapText: true }, border: box },
  otherSection: { font: { bold: true, sz: 14, color: { rgb: 'C2540C' } }, fill: { fgColor: { rgb: 'FBEBDD' } }, alignment: { horizontal: 'center', vertical: 'center' }, border: box },
  otherCell: { font: { sz: 14, color: { rgb: 'C2540C' } }, alignment: { horizontal: 'left', vertical: 'center', wrapText: true }, border: box },
  footer: { font: { italic: true, sz: 10, color: { rgb: '6B7A80' } }, alignment: { horizontal: 'left' } },
};

// group items by the catalogue's canonical category order (used for OTHER / REMOVED blocks).
export function groupByCategory<T extends { category?: string | null }>(items: T[], categoryOrder: string[]): T[] {
  const groups = new Map<string, T[]>();
  const firstSeen: string[] = [];
  for (const e of items) {
    const cat = e.category ?? '';
    if (!groups.has(cat)) { groups.set(cat, []); firstSeen.push(cat); }
    groups.get(cat)!.push(e);
  }
  const rank = (c: string) => { const i = categoryOrder.indexOf(c); return i === -1 ? Number.MAX_SAFE_INTEGER : i; };
  const cats = [...groups.keys()].sort((a, b) => rank(a) - rank(b) || firstSeen.indexOf(a) - firstSeen.indexOf(b));
  return cats.flatMap((c) => groups.get(c)!);
}

export async function exportAssemblyList(
  detail: OrderDetail,
  _specFields: SpecField[],
  categoryOrder: string[] = [],
  prefs: DatePrefs = currentDatePrefs(),
): Promise<string> {
  const mod: any = await import('xlsx-js-style');
  const XLSX = mod.default ?? mod;
  const s = detail.snapshot;
  // the model's standard equipment, sent with the order (server-side) so we can pre-tick the
  // standard checkboxes without depending on a separate catalogue fetch in the browser.
  const standardEquip = detail.standardEquipment ?? [];
  const sapRaw = detail.order.sapNumber && detail.order.sapNumber !== '?' ? detail.order.sapNumber : '';
  const vessel = s.vessel?.vesselName || '';
  const rev = detail.order.revisionNumber;
  const today = formatDate(localTodayIso(), prefs); // the footer's export date, in the user's format
  const NC = 4;

  const selected = s.equipment.filter((e) => e.selected);
  const matched = new Set<number>();

  // template rows minus the ADDITIONAL REQUESTS section + AR1..AR10 (removed per request).
  const rows = TEMPLATE.filter(
    (r) => !(r.type === 'section' && /ADITIONAL REQUESTS/i.test(r.en)) && !(r.type === 'field' && /^AR\d+$/i.test(r.code ?? '')),
  );
  const fieldRows = rows.map((r, i) => ({ r, i })).filter((x) => x.r.type === 'field');
  const nc = (r: TemplateRow) => norm(r.code);
  const tick: string[] = new Array(rows.length).fill(''); // column C (VARIABLE ✓ / value)
  const note: string[][] = rows.map(() => []); // column A (comment - the specific selected item)

  const setField = (pred: (r: TemplateRow) => boolean, c: string, comment?: string): void => {
    const x = fieldRows.find((fr) => pred(fr.r));
    if (!x) return;
    if (c && !tick[x.i]) tick[x.i] = c;
    if (comment && comment.trim()) note[x.i]!.push(comment.trim());
  };
  const detailOf = (e: EquipLine): string => {
    const hs = resolveHelm(e);
    if (hs) return formatHelmExport(hs);
    const bits = [e.assemblyExportLabel || e.name || `#${e.equipmentId}`];
    if (e.color) bits.push(e.color);
    if (e.quantity && e.quantity > 1) bits.push(`x${e.quantity}`);
    return bits.join(' · ');
  };
  const byCat = (catNorms: string[]) => selected.filter((e) => e.category && catNorms.includes(norm(e.category)));

  // 1) ORDER INFO
  setField((r) => nc(r) === 'MODEL', s.modelName ?? '');
  setField((r) => nc(r) === 'NAME', vessel);
  setField((r) => nc(r) === 'SAP', sapRaw);

  // 2) engines — brand tick + engine count, from the selected engine line
  const engine = selected.find((e) => /\b(mercury|yamaha|volvo|yanmar)\b/i.test(`${e.assemblyExportLabel ?? ''} ${e.name ?? ''}`));
  if (engine) {
    matched.add(engine.equipmentId);
    const hay = `${engine.assemblyExportLabel ?? ''} ${engine.name ?? ''}`;
    const brand = /mercury/i.test(hay) ? 'MERCURY' : /yamaha/i.test(hay) ? 'YAMAHA' : /volvo/i.test(hay) ? 'VOLVO' : 'YANMAR';
    setField((r) => nc(r) === brand, '✓', engine.name ?? '');
    const cnt = /(\d+)\s*[x×]/i.exec(engine.name ?? '');
    if (cnt) setField((r) => nc(r) === 'NUMBEROF', cnt[1]!);
  }

  // 3) colours / tubes — by config category
  const fillCat = (fieldCode: string, catNorms: string[]) => {
    for (const e of byCat(catNorms)) { matched.add(e.equipmentId); setField((r) => nc(r) === fieldCode, '✓', [e.color, e.name].filter(Boolean).join(' · ')); }
  };
  fillCat('UPHOLSTERYCOLOR', ['UPHOLSTERY']);
  fillCat('HULLCOLOR', ['HULLANDDECKCOLOR', 'HULLCOLOR']);
  fillCat('DECKCOLOR', ['HULLANDDECKCOLOR', 'DECKCOLOR']);
  fillCat('TUBES', ['TUBESMATERIAL', 'TUBES']);

  // 4) aft configuration → STERN layout
  for (const e of byCat(['AFTCONFIGURATION'])) { matched.add(e.equipmentId); setField((r) => nc(r) === 'STERN', '✓', e.name ?? ''); }

  // 5) helm / seating → FIRST / SECOND ROW OF SEATS (from structured seats where available)
  for (const e of selected) {
    if (matched.has(e.equipmentId)) continue;
    const hs = resolveHelm(e);
    const isHelm = !!hs || /HELM/.test(norm(e.category));
    if (!isHelm) continue;
    matched.add(e.equipmentId);
    setField((r) => norm(r.en).includes('FIRSTROWOFSEATS'), hs ? String(hs.seatsPerRow || '✓') : '✓', e.name ?? '');
    if (hs && (hs.seatingRows || 1) >= 2) setField((r) => norm(r.en).includes('SECONDROWOFSEATS'), String(hs.seatsPerRow || '✓'));
  }

  // 6) everything else — fuzzy-assign each remaining selected item to its closest template checkbox.
  // score: whole-label containment > all significant tokens present > majority of tokens.
  const STOP = new Set(['AND', 'THE', 'FOR', 'WITH', 'NON', 'SET', 'ONLY', 'CLASS', 'SERIES', 'SYSTEM', 'COLOR', 'PAINT', 'APPLICABLE', 'INDEPENDENT', 'POWER', 'SUPPLY']);
  // fields handled by the special passes above (layout / colours / engines) — kept out of the fuzzy
  // pass so a bare word like BOW or SEATS can't hijack e.g. "Cabin in bow area" or "seats covers".
  const SKIP = new Set(['BOW', 'STERN', 'FIRSTROWOFSEATS', 'SECONDROWOFSEATS', 'UPHOLSTERYCOLOR', 'HULLCOLOR', 'DECKCOLOR', 'TUBES', 'PROTECTIONFENDER', 'FLOORING', 'MERCURY', 'YAMAHA', 'VOLVO', 'YANMAR', 'NUMBEROF', 'PRERIGGING']);
  // brand-only checkboxes tick only when nothing more specific matches (radar → RADAR, not RAYMARINE).
  const BRAND = new Set(['RAYMARINE', 'GARMIN', 'FUSION', 'SIMRAD', 'FURUNO', 'VICTRON']);
  const toks = (v: unknown) => String(v ?? '').toUpperCase().split(/[^A-Z0-9]+/).filter((t) => t.length >= 3 && !STOP.has(t));
  const score = (r: TemplateRow, e: EquipLine): number => {
    if (SKIP.has(norm(r.code))) return 0;
    const label = norm(stripColon(r.en)) || norm(r.code);
    if (label.length < 3) return 0;
    const brand = BRAND.has(label);
    const hay = `${norm(e.name)}|${norm(e.assemblyExportLabel)}`;
    if (hay.includes(label)) return brand ? 30 : 100 + label.length;
    const ft = toks(r.en);
    if (!ft.length) return 0;
    const itToks = new Set([...toks(e.name), ...toks(e.assemblyExportLabel)]);
    const itNorm = norm(e.name) + norm(e.assemblyExportLabel);
    const present = (t: string) => itToks.has(t) || itNorm.includes(t);
    if (!present(ft[0]!)) return 0; // the label's primary noun must be in the item (BOW-tent vs BOW-cabin)
    const hit = ft.filter(present).length;
    if (hit === ft.length) return brand ? 30 : 50 + label.length;
    if (hit >= 2) return 20 + hit;
    return 0;
  };
  for (const e of selected) {
    if (matched.has(e.equipmentId)) continue;
    let bestI = -1;
    let bestScore = 20; // threshold - below this, leave for OTHER
    for (const fr of fieldRows) { const sc = score(fr.r, e); if (sc > bestScore) { bestScore = sc; bestI = fr.i; } }
    if (bestI >= 0) { matched.add(e.equipmentId); if (!tick[bestI]) tick[bestI] = '✓'; note[bestI]!.push(detailOf(e)); }
  }

  // 7) standard equipment — tick the checkboxes for the model's included-by-default items (minus any
  //    removed on this build). standard-item names are verbose spec prose, so match with token-BOUNDARY
  //    precision: a label must line up on whole word boundaries. this avoids "TABLE" hitting
  //    "reTRACTABLE", "SCREEN" hitting "touchSCREEN" or "TOP" hitting "hardTOP", while still matching
  //    concatenated labels like SHOREPOWER (SHORE+POWER). ORDER INFO fields are excluded.
  const INFO = new Set(['MODEL', 'NAME', 'SAP']);
  const removedIds = new Set(s.equipment.filter((e) => e.removedFromStandard).map((e) => e.equipmentId));
  const wordParts = (v: unknown) => String(v ?? '').toUpperCase().split(/[^A-Z0-9]+/).filter((t) => t.length >= 2);
  type Boundary = { n: string; starts: Set<number> };
  const boundaryOf = (name: string): Boundary => {
    let n = '';
    const starts = new Set<number>();
    for (const t of wordParts(name)) { starts.add(n.length); n += t; }
    starts.add(n.length);
    return { n, starts };
  };
  const boundaryHas = (b: Boundary, needle: string): boolean => {
    if (!needle || needle.length < 2) return false;
    for (let i = b.n.indexOf(needle); i >= 0; i = b.n.indexOf(needle, i + 1)) {
      if (b.starts.has(i) && b.starts.has(i + needle.length)) return true;
    }
    return false;
  };
  const scoreStd = (r: TemplateRow, b: Boundary): number => {
    const code = norm(r.code);
    if (SKIP.has(code) || INFO.has(code)) return 0;
    const label = norm(stripColon(r.en)) || code;
    if (label.length < 3) return 0;
    const isBrand = BRAND.has(label);
    if (boundaryHas(b, label)) return isBrand ? 30 : 100 + label.length;
    const ft = toks(r.en);
    if (!ft.length || !boundaryHas(b, ft[0]!)) return 0;
    const hit = ft.filter((t) => boundaryHas(b, t)).length;
    if (hit === ft.length) return isBrand ? 30 : 50 + label.length;
    return 0; // no weak "majority" match for the verbose standard names
  };
  for (const it of standardEquip) {
    if (removedIds.has(it.equipmentId) || matched.has(it.equipmentId)) continue;
    const b = boundaryOf(it.name);
    let bestI = -1;
    let bestScore = 29; // accept confident brand (30) / all-token / whole-label matches; skip weak ones
    for (const fr of fieldRows) { const sc = scoreStd(fr.r, b); if (sc > bestScore) { bestScore = sc; bestI = fr.i; } }
    if (bestI >= 0 && !tick[bestI]) { tick[bestI] = '✓'; note[bestI]!.push(it.name); }
  }

  // ── build the sheet ──────────────────────────────────────────────────────────────
  const data: any[][] = [];
  const merges: any[] = [];
  const styleAt: Record<string, any> = {};
  const put = (r: number, c: number, st: any) => { styleAt[XLSX.utils.encode_cell({ r, c })] = st; };
  const mergeRow = (r: number, c1 = 0, c2 = NC - 1) => merges.push({ s: { r, c: c1 }, e: { r, c: c2 } });
  const rowHeights: any[] = [];
  let R = 0;

  // title
  data.push([`ASSEMBLY LIST - ${sapRaw || 'NO-SAP'} · ${vessel || detail.order.orderNumber} · Rev ${rev}`, '', '', '']);
  mergeRow(R); put(R, 0, S.title); rowHeights[R] = { hpt: 26 }; R++;
  // column header row  (COMMENTS  ENG  VARIABLE  GR)
  data.push(['COMMENTS', 'ENG', 'VARIABLE', 'GR']);
  put(R, 0, S.colhdrGr); put(R, 1, S.colhdrEng); put(R, 2, S.colhdr); put(R, 3, S.colhdrGr); R++;

  const SUPERS = new Set(['SET UP:', 'EXTRAS:']);
  rows.forEach((row, i) => {
    if (row.type === 'section') {
      const st = SUPERS.has(row.en) ? S.super : S.sectionEng;
      data.push([row.en, row.en, '', row.gr || '']);
      [0, 1, 2, 3].forEach((c) => put(R, c, st));
      R++;
      return;
    }
    // column A = the specific selected item(s) that ticked this checkbox (the "comment").
    data.push([(note[i] ?? []).join('; '), row.en, tick[i] ?? '', row.gr || '']);
    put(R, 0, S.note); put(R, 1, S.eng); put(R, 2, S.value); put(R, 3, S.gr);
    R++;
  });

  // OTHER SELECTED EQUIPMENT — selected items that didn't map to a template row (nothing lost)
  const other = groupByCategory(selected.filter((e) => !matched.has(e.equipmentId)), categoryOrder);
  data.push(['', '', '', '']); R++;
  data.push(['', 'OTHER SELECTED EQUIPMENT', '', 'ΕΠΙΠΛΕΟΝ ΕΠΙΛΟΓΕΣ']);
  [0, 1, 2, 3].forEach((c) => put(R, c, S.otherSection)); R++;
  if (other.length === 0) { data.push(['', '- none -', '', '']); [0, 1, 2, 3].forEach((c) => put(R, c, S.otherCell)); R++; }
  for (const e of other) {
    data.push([e.category ?? '', itemLabel(e), e.quantity && e.quantity > 1 ? `x${e.quantity}` : (e.color || '✓'), '']);
    [0, 1, 2, 3].forEach((c) => put(R, c, S.otherCell)); R++;
  }

  // REMOVED STANDARD EQUIPMENT — bold red, always shown
  const removed = groupByCategory(s.equipment.filter((e) => e.removedFromStandard), categoryOrder);
  data.push(['', '', '', '']); R++;
  data.push(['', 'REMOVED STANDARD EQUIPMENT', '', 'ΑΦΑΙΡΕΘΗΚΕ ΑΠΟ ΤΟΝ ΒΑΣΙΚΟ ΕΞΟΠΛΙΣΜΟ']);
  [0, 1, 2, 3].forEach((c) => put(R, c, S.redSection)); R++;
  if (removed.length === 0) { data.push(['', '- none -', '', '']); [0, 1, 2, 3].forEach((c) => put(R, c, S.redCell)); R++; }
  for (const e of removed) {
    data.push([e.category ?? '', itemLabel(e), e.quantity && e.quantity > 1 ? `x${e.quantity}` : '', '']);
    [0, 1, 2, 3].forEach((c) => put(R, c, S.redCell)); R++;
  }

  // footer
  data.push(['', '', '', '']); R++;
  data.push([`Exported ${today} · Rolodex - committed revision ${rev}`, '', '', '']); mergeRow(R); put(R, 0, S.footer); R++;

  const ws = XLSX.utils.aoa_to_sheet(data);
  ws['!cols'] = [{ wch: 44 }, { wch: 42 }, { wch: 12 }, { wch: 40 }];
  ws['!merges'] = merges;
  if (rowHeights.length) ws['!rows'] = rowHeights;
  for (const [addr, st] of Object.entries(styleAt)) {
    if (!ws[addr]) ws[addr] = { t: 's', v: '' };
    ws[addr].s = st;
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Assembly List');
  const filename = `${sapRaw || 'NO-SAP'} - ${vessel || detail.order.orderNumber} - Assembly List - Rev ${rev}.xlsx`;
  XLSX.writeFile(wb, filename);
  return filename;
}
